<template>
    <div>
        <!-- Loading State -->
        <div v-if="isLoadingFetch" class="py-5 text-center">
            <i class="fas fa-spinner fa-spin ml-1 mb-3" style="font-size: 24px;"></i>
            <p style="font-size: 13px;">Loading Data...</p>
        </div>

        <!-- List View -->
        <div v-else>
            <div style="margin-top: 50px; margin-bottom: 50px;" v-if="customFieldsLists.length === 0 || !customFieldsLists">
                <small>Predict Custom Fields Configuration is empty</small>
            </div>
            
            <card 
                class="predict-custom-fields-card active__default__module" 
                :class="{'predict-custom-fields-is-active': item.isDefault}" 
                style="margin-bottom: 16px; position: relative;" 
                v-for="(item) in customFieldsLists" 
                :key="item.id"
            >
                <div v-if="item.isDefault" class="badge-default-custom-fields">Default</div>
                <div>
                    <div style="margin-bottom: 16px;">
                        <p style="font-weight: bold; text-align: left; margin: 0;">
                            {{ item.field_name || `Custom Fields Order #${item.id}` }}
                        </p>
                    </div>
                    
                    <div class="custom-fields-preview-section">
                        <div
                            class="custom-fields-preview-toggle"
                            @click.stop="togglePreviewExpand(item.id)"
                        >
                            <i :class="expandedItems[item.id] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                            <span>Fields Detail ({{ (item.fieldOrder && item.fieldOrder.length) || 0 }} columns)</span>
                        </div>
                        <div
                            v-show="expandedItems[item.id]"
                            class="custom-fields-preview"
                        >
                            <div 
                                v-for="(fieldId, index) in item.fieldOrder" 
                                :key="fieldId"
                                class="field-preview-item"
                            >
                                <span class="field-preview-number">{{ index + 1 }}.</span>
                                <span class="field-preview-label">{{ getFieldNameById(fieldId) }}</span>
                            </div>
                            <div v-if="!item.fieldOrder || item.fieldOrder.length === 0" class="field-preview-empty">
                                No fields selected
                            </div>
                        </div>
                        <div class="custom-fields-actions">
                            <button 
                                class="btn btn-green btn-round btn-icon btn-fab btn-default btn-star-default" 
                                type="button" 
                                @click="setDefaultCustomFields(item.id)"
                                :title="item.isDefault ? 'Already Default' : 'Set as Default'"
                                :disabled="setDefaultLoadingId === item.id"
                            >
                                <i v-if="setDefaultLoadingId === item.id" class="fa-solid fa-spinner fa-spin-pulse" style="font-size: 16px;"></i>
                                <i v-else :class="item.isDefault ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                            </button>
                            <button 
                                class="btn btn-green btn-round btn-icon btn-fab btn-default" 
                                type="button" 
                                @click="openEditModal(item)"
                            >
                                <i class="tim-icons icon-pencil"></i>   
                            </button>
                            <button 
                                class="btn btn-green btn-round btn-icon btn-fab btn-default" 
                                type="button" 
                                @click="processDeleteCustomFields(item.id)"
                            >
                                <i class="el-icon-delete"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </card>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-end mt-2 mb-3">
            <div>
                <p class="card-category">
                    Showing {{ paginationCustomFields.from }} to {{ paginationCustomFields.to }} of {{ paginationCustomFields.total }} entries
                </p>
                <base-pagination 
                    class="pagination-no-border mt-3"
                    v-model="paginationCustomFields.currentPage"
                    :per-page="paginationCustomFields.perPage"
                    :total="paginationCustomFields.total"
                    @input="changePageCustomFields">
                </base-pagination>
            </div>
        </div>

        <!-- Add Button -->
        <div class="d-flex justify-content-end">
            <base-button class="my-2" @click="openAddModal()">
                <i class="fas fa-plus-circle" style="margin: 0 4px 1px 0;"></i>
                Add Configuration
            </base-button>
        </div>

        <!-- Add/Edit Modal -->
        <modal :show.sync="modals.addEditCustomFields" headerClasses="justify-content-center" size="large">
            <h4 slot="header" class="color_predict_custom_fields" style="text-align: center; color: black !important;">
                {{ isEditMode ? 'Edit' : 'Add' }} Custom Fields Configuration
            </h4>
            <el-card shadow="never">
                <div class="custom-fields-modal-content">
                    <!-- Name Input Section -->
                    <div class="custom-fields-section">
                        <div class="custom-fields-title">Configuration Name</div>
                        <el-input
                            v-model="fieldName"
                            placeholder="Enter configuration name"
                            maxlength="255"
                            show-word-limit
                        ></el-input>
                        <div v-if="error.fieldName" style="margin-top: 8px;">
                            <small style="color:#942434 !important;">* Please enter a configuration name</small>
                        </div>
                    </div>

                    <el-divider></el-divider>

                    <!-- Select Fields Section -->
                    <div class="custom-fields-section">
                        <div class="custom-fields-title">Select custom fields</div>
                        <div class="custom-fields-list">
                            <label
                                v-for="field in masterFields"
                                :key="field.id"
                                class="custom-field-item"
                            >
                                <input
                                    type="checkbox"
                                    :value="field.id"
                                    v-model="selectedFieldIds"
                                    @change="updateSelectedOrder"
                                />
                                <span>{{ field.field_name }}</span>
                            </label>
                        </div>
                    </div>

                    <el-divider></el-divider>

                    <!-- Selected Order Section -->
                    <div class="custom-fields-section custom-fields-order-section">
                        <div class="custom-fields-order-title">Selected order</div>
                        <div v-if="selectedFieldIds.length === 0" class="custom-fields-empty">
                            No fields selected.
                        </div>
                        <div
                            v-else
                            class="custom-fields-order-list"
                            @dragover.prevent
                            @drop="handleDrop"
                        >
                            <div
                                v-for="(fieldId, index) in selectedFieldIds"
                                :key="fieldId"
                                class="custom-fields-order-item"
                                :class="{
                                    'is-dragging': draggedIndex === index,
                                    'is-drag-over': dragOverIndex === index && draggedIndex !== index
                                }"
                                draggable="true"
                                @dragstart="handleDragStart(index, $event)"
                                @dragend="handleDragEnd"
                                @dragover.prevent="handleDragOver(index)"
                                @dragleave="handleDragLeave"
                            >
                                <span class="order-label">
                                    <i class="el-icon-rank drag-handle"></i>
                                    {{ index + 1 }}. {{ getFieldNameById(fieldId) }}
                                </span>
                                <div class="order-actions">
                                    <el-button
                                        size="mini"
                                        :disabled="index === 0"
                                        @click="moveField(fieldId, 'up')"
                                    >
                                        Up
                                    </el-button>
                                    <el-button
                                        size="mini"
                                        :disabled="index === selectedFieldIds.length - 1"
                                        @click="moveField(fieldId, 'down')"
                                    >
                                        Down
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Validation Error -->
                    <div v-if="error.fieldOrder" style="margin-top: 12px;">
                        <small style="color:#942434 !important;">* Please select at least one field</small>
                    </div>
                </div>

                <div class="text-right" style="margin-top: 20px;">
                    <base-button 
                        class="pull-right mt-3 mb-4" 
                        :disabled="isLoadingAddEdit" 
                        style="height:40px;" 
                        @click="processSaveCustomFields()"
                    >
                        {{ isEditMode ? 'Update' : 'Save' }}
                        <i v-if="isLoadingAddEdit" class="fas fa-spinner fa-spin ml-1" style="font-size: 16px;"></i>
                    </base-button>
                </div>
            </el-card>
        </modal>
    </div>
</template>

<script>
import { Card, MessageBox } from 'element-ui';
import Modal from '../Modal.vue';
import { Button, Divider, Input } from 'element-ui';
import BasePagination from '../BasePagination.vue';

export default {
    name: 'PredictCustomFieldsManager',
    components: {
        Modal,
        BasePagination,
        [Card.name]: Card,
        [Button.name]: Button,
        [Divider.name]: Divider,
        [Input.name]: Input
    },
    data() {
        return {
            isLoadingFetch: true,
            isLoadingAddEdit: false,
            customFieldsLists: [],
            masterFields: [],
            paginationCustomFields: {
                currentPage: 1,
                perPage: 3,
                total: 0,
                from: 0,
                to: 0,
                lastPage: 1
            },
            modals: {
                addEditCustomFields: false
            },
            isEditMode: false,
            editingId: null,
            fieldName: '',
            selectedFieldIds: [],
            draggedIndex: null,
            dragOverIndex: null,
            expandedItems: {},
            error: {
                fieldName: false,
                fieldOrder: false
            },
            setDefaultLoadingId: null
        };
    },
    mounted() {
        this.getCustomFieldsList();
        this.getMasterFields();
    },
    methods: {
        async getCustomFieldsList(skipLoading = false) {
            if (!skipLoading) {
                this.isLoadingFetch = true;
            }
            try {
                const response = await this.$store.dispatch('getPredictCustomFieldsList', {
                    companyId: this.$store.getters.userData.company_id,
                    page: this.paginationCustomFields.currentPage,
                    perPage: this.paginationCustomFields.perPage
                });
                
                this.paginationCustomFields.currentPage = response.customFieldsLists.current_page;
                this.paginationCustomFields.total = response.customFieldsLists.total;
                this.paginationCustomFields.lastPage = response.customFieldsLists.last_page;
                this.paginationCustomFields.from = response.customFieldsLists.from ? response.customFieldsLists.from : 0;
                this.paginationCustomFields.to = response.customFieldsLists.to ? response.customFieldsLists.to : 0;
                
                this.customFieldsLists = response.customFieldsLists.data.map(item => {
                    const mappedItem = {
                        ...item,
                        isDefault: item.is_default || false,
                        fieldOrder: item.field_order ? JSON.parse(item.field_order) : []
                    };
                    // Set default expanded untuk semua items
                    this.$set(this.expandedItems, mappedItem.id, true);
                    return mappedItem;
                });
            } catch (error) {
                console.error('Failed to fetch custom fields list:', error);
                this.customFieldsLists = [];
                this.$notify({ type: 'danger', message: 'Something Wrong Get Custom Fields List', icon: 'fas fa-bug' });
            } finally {
                if (!skipLoading) {
                    this.isLoadingFetch = false;
                }
            }
        },
        async getMasterFields() {
            try {
                const response = await this.$store.dispatch('getPredictCustomFieldsMasters');
                this.masterFields = response.masterFields || [];
            } catch (error) {
                console.error('Failed to fetch master fields:', error);
                this.masterFields = [];
            }
        },
        changePageCustomFields() {
            this.getCustomFieldsList();
        },
        openAddModal() {
            this.isEditMode = false;
            this.editingId = null;
            this.fieldName = '';
            this.selectedFieldIds = [];
            this.error.fieldName = false;
            this.error.fieldOrder = false;
            this.modals.addEditCustomFields = true;
        },
        openEditModal(item) {
            this.isEditMode = true;
            this.editingId = item.id;
            this.fieldName = item.field_name || '';
            this.selectedFieldIds = item.fieldOrder ? [...item.fieldOrder] : [];
            this.error.fieldName = false;
            this.error.fieldOrder = false;
            this.modals.addEditCustomFields = true;
        },
        async processSaveCustomFields() {
            // Validation
            let hasError = false;
            if (!this.fieldName || this.fieldName.trim() === '') {
                this.error.fieldName = true;
                hasError = true;
            } else {
                this.error.fieldName = false;
            }
            
            if (this.selectedFieldIds.length === 0) {
                this.error.fieldOrder = true;
                hasError = true;
            } else {
                this.error.fieldOrder = false;
            }

            if (hasError) {
                return;
            }

            this.isLoadingAddEdit = true;
            try {
                if (this.isEditMode) {
                    await this.$store.dispatch('updatePredictCustomFields', {
                        companyId: this.$store.getters.userData.company_id,
                        id: this.editingId,
                        fieldName: this.fieldName.trim(),
                        fieldOrder: this.selectedFieldIds
                    });
                    this.$notify({ type: 'success', message: 'Update Custom Fields Configuration Successfully', icon: 'fas fa-check' });
                } else {
                    await this.$store.dispatch('createPredictCustomFields', {
                        companyId: this.$store.getters.userData.company_id,
                        fieldName: this.fieldName.trim(),
                        fieldOrder: this.selectedFieldIds
                    });
                    this.$notify({ type: 'success', message: 'Add Custom Fields Configuration Successfully', icon: 'fas fa-check' });
                }
                
                this.modals.addEditCustomFields = false;
                this.getCustomFieldsList();
            } catch (error) {
                console.error('Failed to save custom fields:', error);
                const message = (error.response && error.response.data && error.response.data.message) || 'Something Wrong Save Custom Fields Configuration';
                this.$notify({ type: 'danger', message, icon: 'fas fa-bug' });
            } finally {
                this.isLoadingAddEdit = false;
            }
        },
        async processDeleteCustomFields(id) {
            MessageBox.confirm('Are you sure you want to delete this custom fields configuration?', 'Confirm Delete', {
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                type: 'warning'
            }).then(async () => {
                try {
                    await this.$store.dispatch('deletePredictCustomFields', { 
                        companyId: this.$store.getters.userData.company_id,
                        id 
                    });
                    this.$notify({ type: 'success', message: 'Delete Custom Fields Configuration Successfully', icon: 'fas fa-check' });
                    this.getCustomFieldsList();
                } catch (error) {
                    console.error('Failed to delete custom fields:', error);
                    const message = (error.response && error.response.data && error.response.data.message) || 'Something Wrong Delete Custom Fields Configuration';
                    this.$notify({ type: 'danger', message, icon: 'fas fa-bug' });
                }
            }).catch(() => {});
        },
        async setDefaultCustomFields(id) {
            this.setDefaultLoadingId = id;
            try {
                const currentItem = this.customFieldsLists.find(item => item.id === id);
                const isCurrentlyDefault = currentItem && currentItem.isDefault;
                
                await this.$store.dispatch('setDefaultPredictCustomFields', { 
                    companyId: this.$store.getters.userData.company_id,
                    id 
                });
                await this.getCustomFieldsList(true);
                
                const message = isCurrentlyDefault 
                    ? 'Default Custom Fields Configuration Reset Successfully' 
                    : 'Set Default Custom Fields Configuration Successfully';
                this.$notify({ type: 'success', message, icon: 'fas fa-check' });
            } catch (error) {
                console.error('Failed to set default custom fields:', error);
                const message = (error.response && error.response.data && error.response.data.message) || 'Something Wrong Set Default Custom Fields Configuration';
                this.$notify({ type: 'danger', message, icon: 'fas fa-bug' });
            } finally {
                this.setDefaultLoadingId = null;
            }
        },
        getFieldNameById(fieldId) {
            const field = this.masterFields.find(f => f.id === fieldId);
            return field ? field.field_name : `Field #${fieldId}`;
        },
        updateSelectedOrder() {
            // Keep only selected fields in order
            this.selectedFieldIds = this.selectedFieldIds.filter(id => 
                this.masterFields.some(f => f.id === id)
            );
        },
        moveField(fieldId, direction) {
            const index = this.selectedFieldIds.indexOf(fieldId);
            if (index === -1) return;
            
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= this.selectedFieldIds.length) return;
            
            const list = [...this.selectedFieldIds];
            [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
            this.selectedFieldIds = list;
        },
        handleDragStart(index, event) {
            this.draggedIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            if (event.target.classList.contains('custom-fields-order-item')) {
                event.target.style.opacity = '0.5';
            } else {
                const item = event.target.closest('.custom-fields-order-item');
                if (item) item.style.opacity = '0.5';
            }
        },
        handleDragEnd(event) {
            const item = event.target.closest('.custom-fields-order-item');
            if (item) item.style.opacity = '1';
            this.draggedIndex = null;
            this.dragOverIndex = null;
        },
        handleDragOver(index) {
            if (this.draggedIndex !== null && this.draggedIndex !== index) {
                this.dragOverIndex = index;
            }
        },
        handleDragLeave() {
            this.dragOverIndex = null;
        },
        handleDrop(event) {
            event.preventDefault();
            if (this.draggedIndex === null || this.dragOverIndex === null) return;
            
            const list = [...this.selectedFieldIds];
            const [item] = list.splice(this.draggedIndex, 1);
            list.splice(this.dragOverIndex, 0, item);
            this.selectedFieldIds = list;
            
            this.draggedIndex = null;
            this.dragOverIndex = null;
        },
        togglePreviewExpand(itemId) {
            this.$set(this.expandedItems, itemId, !this.expandedItems[itemId]);
        }
    }
};
</script>

<style scoped>
.predict-custom-fields-card {
    transition: all 0.3s ease;
    position: relative;
}

.predict-custom-fields-is-active {
    border: 2px solid #3b82f6 !important;
    border-radius: 8px;
    background-color: transparent !important;
}

.predict-custom-fields-is-active >>> .card {
    border: 2px solid #3b82f6 !important;
    border-radius: 8px;
    background-color: transparent !important;
}

.predict-custom-fields-is-active >>> .card-body {
    border-radius: 8px;
    background-color: transparent !important;
}

.badge-default-custom-fields {
    position: absolute;
    top: 12px;
    right: 12px;
    background-color: #3b82f6;
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    z-index: 10;
}

.custom-fields-preview-section {
    margin-top: 12px;
    position: relative;
}

.custom-fields-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 12px;
}

.custom-fields-actions .btn-star-default {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
}

.custom-fields-actions .btn-star-default i {
    margin: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.custom-fields-preview-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #3b82f6;
    cursor: pointer;
    padding: 6px 0;
    user-select: none;
    transition: color 0.2s ease;
}

.custom-fields-preview-toggle:hover {
    color: #2563eb;
}

.custom-fields-preview-toggle i {
    font-size: 14px;
}

.custom-fields-preview {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    background-color: #f9fafb;
    border-radius: 6px;
    margin-top: 8px;
    max-height: 210px;
    overflow-y: auto;
}

.field-preview-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background-color: white;
    border-radius: 4px;
    font-size: 13px;
}

.field-preview-number {
    font-weight: 600;
    color: #6b7280;
    min-width: 24px;
}

.field-preview-label {
    color: #111827;
}

.field-preview-empty {
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    padding: 12px;
}

.custom-fields-modal-content {
    max-height: 600px;
    overflow-y: auto;
}

.custom-fields-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.custom-fields-title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 2px;
}

.custom-fields-list {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px 16px;
}

.custom-field-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #111827;
    cursor: pointer;
    padding: 4px 0;
}

.custom-field-item input[type="checkbox"] {
    cursor: pointer;
}

.custom-fields-order-section {
    margin-top: 12px;
}

.custom-fields-order-title {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

.custom-fields-empty {
    font-size: 13px;
    color: #6b7280;
    padding: 20px;
    text-align: center;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px dashed #e5e7eb;
}

.custom-fields-order-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 500px;
    overflow-y: auto;
    padding-right: 4px;
}

.custom-fields-order-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background-color: #ffffff;
    transition: background-color 0.2s ease;
    cursor: grab;
}

.custom-fields-order-item:active {
    cursor: grabbing;
}

.custom-fields-order-item.is-dragging {
    opacity: 0.5;
    cursor: grabbing;
}

.custom-fields-order-item.is-drag-over {
    border-color: #3b82f6;
    background-color: #eff6ff;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.order-label {
    font-size: 13px;
    color: #111827;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
}

.drag-handle {
    color: #9ca3af;
    cursor: grab;
    font-size: 16px;
}

.drag-handle:active {
    cursor: grabbing;
}

.order-actions {
    display: flex;
    gap: 6px;
}

.custom-fields-modal-content::-webkit-scrollbar,
.custom-fields-order-list::-webkit-scrollbar {
    width: 6px;
}

.custom-fields-modal-content::-webkit-scrollbar-track,
.custom-fields-order-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.custom-fields-modal-content::-webkit-scrollbar-thumb,
.custom-fields-order-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.custom-fields-modal-content::-webkit-scrollbar-thumb:hover,
.custom-fields-order-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.custom-fields-preview::-webkit-scrollbar {
    width: 6px;
}

.custom-fields-preview::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.custom-fields-preview::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.custom-fields-preview::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>
